import React, { useCallback, useMemo, useRef } from 'react';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import PeriodRenderComponent, {
  checkMoreThanOneYear,
} from 'npo/pages/ReportBuilder/Components/SectionPeriod/PeriodRenderComponent';
import { ParentPeriodValue } from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import { formattedDate } from 'shared/utils/date';
import { ReportsTable } from '../ReportGroup';

export const PERIOD_QUESTION_ID = 10003;
const PROGRAM_DURATION_QUESTION_ID = 10004;

interface Props {
  questionId: number;
  enumOptions: { response: string }[];
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  report?: ReportsTable;
}

const serializePeriod = (value: ParentPeriodValue | null): string => {
  if (!value || !value.startDate || !value.endDate) return '';
  return `${capitalizeFirstLetter(value.option)}: ${formattedDate(
    value.startDate,
  )} - ${formattedDate(value.endDate)}`;
};

const PeriodField: React.FC<Props> = ({
  questionId,
  enumOptions,
  value,
  onChange,
  report,
}) => {
  const { answersTextById } = useProgram();

  // Stabilize options so PeriodRenderComponent's memoized parse/effect deps
  // don't re-fire on every parent render.
  const options = useMemo(() => enumOptions.map(x => x.response), [
    enumOptions,
  ]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const handleChange = useCallback(
    (_qid: number, next: ParentPeriodValue | null) => {
      const serialized = serializePeriod(next);
      if (serialized === (valueRef.current.text ?? '')) return;
      onChangeRef.current({ ...valueRef.current, text: serialized });
    },
    [],
  );

  return (
    <PeriodRenderComponent
      reportId={report?.id ?? 0}
      isThirdParty={false}
      onChange={handleChange}
      options={options}
      questionId={questionId}
      value={value.text ?? null}
      reportYear={report?.year?.toString()}
      isMoreThanOneYear={checkMoreThanOneYear(
        answersTextById[PROGRAM_DURATION_QUESTION_ID],
      )}
    />
  );
};

export default PeriodField;
